import { useContext, useEffect, useState } from 'react';

import { OptimizelyContext } from '@optimizely/react-sdk';

import { FLAG } from 'Utilities/analytics/optimizely/constants';

import type { ValidOfferProps } from '../../acceptOffer/AcceptOffer.types';

import { DEALER_RATING_VARIATION_KEYS, getIsReservePriceMet } from './DealerRatingsExperiment.helpers';

export const useDealerRatingsExperiment = ({
  offer,
  vehicleEnquiry,
}: {
  offer?: ValidOfferProps['offer'];
  vehicleEnquiry?: ValidOfferProps['vehicleEnquiry'];
}) => {
  const { optimizely } = useContext(OptimizelyContext);

  const [decision, setDecision] = useState<{
    decided: boolean;
    decisionAttributes?: Record<string, any>;
    enabled: boolean;
    variationKey: string | null;
  }>({
    decided: false,
    decisionAttributes: {},
    enabled: false,
    variationKey: null,
  });

  useEffect(() => {
    if (!offer || !vehicleEnquiry) {
      return;
    }

    const { closingBid, enquiryId } = offer;
    const dealerId = closingBid?.dealerContact?.dealer?.id;

    const decisionAttributes = {
      overrideAttributes: {
        reserve_met: getIsReservePriceMet({ offer, vehicleEnquiry }),
        winning_dealer_id: dealerId ?? 0,
      },
      overrideUserId: String(enquiryId),
    };

    optimizely?.onReady().then(() => {
      const { enabled, variationKey } = optimizely.decide(
        FLAG.DEALER_RATINGS_CONSISTENCY_EXPERIMENT,
        undefined,
        String(enquiryId),
        { ...decisionAttributes.overrideAttributes },
      );

      setDecision({ decided: true, decisionAttributes, enabled, variationKey });
    });
  }, [optimizely, offer, vehicleEnquiry]);

  const { decided, decisionAttributes = {}, enabled, variationKey } = decision;

  const isEligibleForDealerRatingsExperiment = variationKey
    ? variationKey !== DEALER_RATING_VARIATION_KEYS.OFF
    : false;

  return {
    decided,
    decisionAttributes,
    enabled,
    isEligibleForDealerRatingsExperiment,
    variationKey,
  };
};
